import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Fuel, Settings2, Loader2, Sparkles, Images, ChevronRight, KeyRound } from 'lucide-react';
import { api, useAuthStore } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { FuelReviewDialog } from '../components/fuel/FuelReviewDialog';
import { AuthImg, money } from '../components/fuel/fuelUtils';

const TABS = [['pending', 'Bekleyen'], ['approved', 'Onaylanan'], ['rejected', 'Reddedilen']];
const AI = { idle: ['Sırada', 'bg-slate-100 text-slate-600'], running: ['Okunuyor', 'bg-violet-100 text-violet-700'], done: ['Okundu', 'bg-emerald-100 text-emerald-700'], error: ['Okunamadı', 'bg-rose-100 text-rose-700'] };

function AiSettings({ open, onClose }) {
  const isSuper = useAuthStore((s) => s.user?.role) === 'SUPER_ADMIN';
  const [cfg, setCfg] = useState(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setKey(''); api.get('/fuel-submissions/settings/ai').then((r) => setCfg(r.data.data)); } }, [open]);
  const save = async () => {
    setBusy(true);
    try {
      const body = { mode: cfg.mode, auto_read: cfg.auto_read, ...(key ? { anthropic_key: key } : {}) };
      setCfg((await api.put('/fuel-submissions/settings/ai', body)).data.data);
      toast.success('Fiş okuma ayarları kaydedildi'); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kaydedilemedi'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="fuel-ai-settings">
        <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" />Fiş Okuma (Yapay Zeka)</DialogTitle>
        <DialogDescription>Fişler Claude ile okunur; okunamayan alanları elle doldurursunuz.</DialogDescription>
        {!cfg ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-3">
            {[['emergent', 'Emergent anahtarı (hazır)', 'Ek ayar gerekmez, kullanım bakiyenizden düşer'], ['own', 'Kendi Claude API anahtarım', 'Anthropic hesabınızdan alınan sk-ant-… anahtarı']].map(([k, l, d]) => (
              <label key={k} className={`flex gap-2 rounded-xl border p-3 cursor-pointer ${cfg.mode === k ? 'border-violet-500 bg-violet-50/50' : 'border-slate-200'}`} data-testid={`fuel-ai-mode-${k}`}>
                <input type="radio" checked={cfg.mode === k} disabled={!isSuper} onChange={() => setCfg({ ...cfg, mode: k })} className="mt-1" />
                <span><b className="text-sm">{l}</b><span className="block text-[11px] text-slate-500">{d}</span></span>
              </label>
            ))}
            {cfg.mode === 'own' && (
              <label className="block text-sm"><span className="flex items-center gap-1 font-semibold"><KeyRound className="h-3.5 w-3.5" />Claude API anahtarı</span>
                <input type="password" value={key} disabled={!isSuper} onChange={(e) => setKey(e.target.value)} placeholder={cfg.key_masked || 'sk-ant-...'} className="mt-1 w-full h-9 rounded-md border border-slate-300 px-2 text-sm" data-testid="fuel-ai-key" />
                {cfg.has_own_key && <span className="text-[11px] text-emerald-700">Kayıtlı anahtar: {cfg.key_masked} (değiştirmek için yenisini yazın)</span>}
              </label>
            )}
            <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 text-sm"><span><b>Şoför gönderince otomatik oku</b><span className="block text-[11px] text-slate-500">Kapalıysa resme tıklayıp "Bu fişi oku" ile okutursunuz</span></span>
              <Switch checked={cfg.auto_read} disabled={!isSuper} onCheckedChange={(v) => setCfg({ ...cfg, auto_read: v })} data-testid="fuel-ai-auto" /></label>
            <p className="text-[11px] text-slate-500">Model: {cfg.model}</p>
          </div>
        )}
        {isSuper && <DialogFooter><Button variant="ghost" onClick={onClose}>Vazgeç</Button><Button onClick={save} disabled={busy || !cfg} data-testid="fuel-ai-save">Kaydet</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

function Row({ r, onOpen }) {
  const [l, cls] = AI[r.ai_status] || AI.idle;
  return (
    <button type="button" onClick={() => onOpen(r.id)} className="w-full text-left rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 flex items-center gap-3 hover:border-orange-400 transition-colors" data-testid={`fuel-sub-row-${r.id}`}>
      <div className="h-16 w-12 rounded-lg overflow-hidden shrink-0 bg-slate-100">{r.images?.[0] && <AuthImg src={`/fuel-submissions/${r.id}/images/${r.images[0].id}`} className="h-full w-full object-cover object-top" />}</div>
      <div className="flex-1 min-w-0">
        <div className="font-bold truncate">{r.employee_name}</div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
          <span>{new Date(r.created_at).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' })}</span>
          <span className="tabular-nums">{r.plate || 'plaka yok'}</span>
          <span className="inline-flex items-center gap-0.5"><Images className="h-3 w-3" />{r.image_count}</span>
          {r.km_driver && <span className="tabular-nums">{r.km_driver} km</span>}
          {r.note && <span className="truncate max-w-[200px] italic" title={r.note}>“{r.note}”</span>}
        </div>
      </div>
      <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${cls}`}>{l}</span>
      <div className="text-right tabular-nums"><div className="font-black">{money(r.total)}</div><div className="text-[11px] text-slate-500">{r.liters} L</div></div>
      <ChevronRight className="h-4 w-4 text-slate-300" />
    </button>
  );
}

export default function FuelSubmissions() {
  const [tab, setTab] = useState('pending');
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [settings, setSettings] = useState(false);
  const load = useCallback(() => api.get('/fuel-submissions', { params: { status: tab } }).then((r) => setData(r.data.data)).catch((e) => toast.error(e?.response?.data?.detail || 'Yüklenemedi')), [tab]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  return (
    <div className="space-y-4" data-testid="fuel-submissions-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-orange-500 text-white flex items-center justify-center"><Fuel className="h-6 w-6" /></div>
        <div className="flex-1"><h1 className="text-2xl font-black">Mazot Bildirimleri</h1><p className="text-sm text-slate-500">Şoförlerden gelen fişleri kontrol edip onaylayın; onaylananlar Yakıt Raporu'na yazılır.</p></div>
        <Button variant="outline" onClick={() => setSettings(true)} data-testid="fuel-ai-settings-btn"><Settings2 className="h-4 w-4 mr-1" />Fiş okuma ayarları</Button>
      </div>
      <div className="inline-flex rounded-xl border border-slate-200 bg-white dark:bg-slate-900 p-1 gap-1">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`fuel-tab-${k}`} className={`h-9 px-3 rounded-lg text-sm font-semibold ${tab === k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
            {l}{data?.counts?.[k] != null && <span className="ml-1.5 text-[11px] opacity-70">{data.counts[k]}</span>}
          </button>
        ))}
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : data.rows.length === 0 ? <p className="text-sm text-slate-400 py-12 text-center" data-testid="fuel-empty">Bildirim yok</p> : (
        <div className="grid gap-2 max-w-4xl">{data.rows.map((r) => <Row key={r.id} r={r} onOpen={setOpenId} />)}</div>
      )}
      {openId && <FuelReviewDialog id={openId} onClose={() => { setOpenId(null); load(); }} onDone={() => { setOpenId(null); load(); }} />}
      <AiSettings open={settings} onClose={() => setSettings(false)} />
    </div>
  );
}
